import React, { useEffect, useState } from 'react';

const DownloadApp = () => {
  const userAgent = navigator.userAgent || navigator.vendor || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /android/i.test(userAgent);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const check = () => setWidth(window.innerWidth);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const isMobile = width < 576;
  const isTablet = width >= 576 && width < 1024;
  const isDesktop = width >= 1024;

  const playStoreMarketURL = 'market://details?id=com.tringbox.tringuser';
  const playStoreWebURL =
    'https://play.google.com/store/apps/details?id=com.tringbox.tringuser';
  const playStoreURL = isAndroid ? playStoreMarketURL : playStoreWebURL;
  const appStoreURL = 'https://apps.apple.com/in/app/tringbox/id6748858527';

  const handleDownload = () => {
    if (isIOS) {
      window.location.href = appStoreURL;
    } else if (isAndroid) {
      window.location.href = playStoreMarketURL;
      setTimeout(() => {
        window.location.href = playStoreWebURL;
      }, 1000);
    } else {
      window.location.href = playStoreWebURL;
    }
  };

  const features = [
    {
      emoji: '🎵',
      title: 'Request Songs',
      desc: 'Pick your favourite track and hear it live at the venue',
    },
    {
      emoji: '📣',
      title: 'Send Shoutouts',
      desc: 'Dedicate a shoutout to your friends on the big screen',
    },
    {
      emoji: '🎧',
      title: 'Your Mood, Your Music',
      desc: 'Set the vibe and let the DJ play what you love',
    },
    {
      emoji: '🎁',
      title: 'Win Rewards',
      desc: 'Earn coins, spin the wheel, and unlock exciting prizes',
    },
  ];

  const logoSize = isMobile ? '190px' : isTablet ? '190px' : '220px';
  const h1Size = isMobile
    ? 'clamp(28px, 8vw, 36px)'
    : isTablet
      ? '38px'
      : '50px';
  const bodySize = isMobile ? '14px' : isTablet ? '16px' : '18px';
  const featureCols = isDesktop ? 'repeat(2, 1fr)' : '1fr';

  // Store badge shared style
  const storeBadgeBase = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '12px',
    padding: '10px 18px',
    textDecoration: 'none',
    border: '1.5px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    transition: 'all 0.2s ease',
    minWidth: isMobile ? '130px' : '150px',
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;600;700;800&display=swap');
          * { box-sizing: border-box; }

          @keyframes downloadFloat {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-12px); }
          }
          @keyframes downloadFadeUp {
            from { opacity: 0; transform: translateY(28px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes downloadPulse {
            0%, 100% { box-shadow: 0 4px 24px rgba(204,21,30,0.35); }
            50% { box-shadow: 0 10px 40px rgba(204,21,30,0.6); }
          }
          @keyframes downloadShimmer {
            0% { left: -100%; }
            100% { left: 150%; }
          }
          .dl-btn:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 12px 48px rgba(204,21,30,0.6) !important;
          }
          .dl-badge:hover {
            background: rgba(255,255,255,0.12) !important;
            border-color: rgba(255,255,255,0.4) !important;
            transform: translateY(-2px);
          }
          .dl-card:hover {
            transform: translateY(-5px) !important;
            border-color: rgba(204,21,30,0.35) !important;
            background: rgba(255,255,255,0.07) !important;
          }
        `}
      </style>

      <div
        style={{
          minHeight: '100dvh',
          width: '100%',
          background:
            'linear-gradient(160deg, #4a0c18 0%, #3a0810 40%, #4a0c18 80%)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Quicksand', sans-serif",
          overflowX: 'hidden',
        }}
      >
        {/* ── DESKTOP: side-by-side hero ── */}
        {isDesktop && (
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '64px 100px',
              gap: '72px',
              minHeight: '100dvh',
            }}
          >
            {/* glow orbs */}
            <div
              style={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: '500px',
                height: '500px',
                background:
                  'radial-gradient(circle, rgba(204,21,30,0.14) 0%, transparent 65%)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: '-10%',
                width: '400px',
                height: '400px',
                background:
                  'radial-gradient(circle, rgba(138,50,93,0.1) 0%, transparent 65%)',
                pointerEvents: 'none',
              }}
            />

            {/* Left */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                maxWidth: '520px',
                zIndex: 1,
                animation: 'downloadFadeUp 0.7s ease-out both',
              }}
            >
              <div
                style={{
                  width: logoSize,
                  height: logoSize,
                  background: 'white',
                  WebkitMask: 'url(/images/TringboxWhite.svg) no-repeat center',
                  mask: 'url(/images/TringboxWhite.svg) no-repeat center',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  marginBottom: '28px',
                  flexShrink: 0,
                }}
              />

              <h1
                style={{
                  color: '#fff',
                  fontSize: h1Size,
                  fontWeight: 800,
                  margin: '0 0 16px 0',
                  lineHeight: 1.15,
                  letterSpacing: '-0.5px',
                  whiteSpace: 'nowrap',
                }}
              >
                Your Music, Your Mood
              </h1>

              <p
                style={{
                  color: '#e0e0e0',
                  fontSize: bodySize,
                  margin: '0 0 36px 0',
                  lineHeight: 1.75,
                  maxWidth: '440px',
                }}
              >
                Request your favourite songs, send shoutouts to friends, and set
                the vibe at live venues all from the{' '}
                <span style={{ color: '#fff', fontWeight: 700 }}>Tringbox</span>{' '}
                app.
              </p>

              {/* Download button */}
              <button
                className="dl-btn"
                onClick={handleDownload}
                style={{
                  background:
                    'linear-gradient(90deg, #CC151E 0%, #8A325D 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '17px 52px',
                  fontSize: '18px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Quicksand', sans-serif",
                  animation: 'downloadPulse 3s ease-in-out infinite',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  marginBottom: '24px',
                  letterSpacing: '0.3px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '60%',
                    height: '100%',
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                    animation: 'downloadShimmer 2.5s infinite',
                  }}
                />
                <svg
                  style={{ position: 'relative', zIndex: 1 }}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span style={{ position: 'relative', zIndex: 1 }}>
                  Download the App
                </span>
              </button>

              {/* Store badges */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  className="dl-badge"
                  href={playStoreURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={storeBadgeBase}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 20.5v-17c0-.83 1-.83 1.5-.5l15 8.5-15 8.5c-.5.33-1.5.33-1.5-.5z"
                      fill="#34A853"
                    />
                    <path d="M3 3.5L13.5 12 3 20.5V3.5z" fill="#FBBC04" />
                    <path
                      d="M3 3.5L13.5 12 8 17.5 3 20.5V3.5z"
                      fill="#EA4335"
                    />
                    <path d="M3 20.5L8 15.5l5.5 6.5-10.5-1.5z" fill="#4285F4" />
                  </svg>
                  <div>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '9px',
                        lineHeight: 1,
                        marginBottom: '2px',
                      }}
                    >
                      GET IT ON
                    </div>
                    <div
                      style={{
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      Google Play
                    </div>
                  </div>
                </a>
                <a
                  className="dl-badge"
                  href={appStoreURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={storeBadgeBase}
                >
                  <svg
                    width="20"
                    height="22"
                    viewBox="0 0 814 1000"
                    fill="white"
                  >
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 71 0 130.5 46.4 174.9 46.4 42.7 0 109.2-49.8 190.5-49.8zM642 110.3c32.5-38.2 56.3-91.4 56.3-144.6 0-7.7-.6-15.4-1.9-22.4-53.4 2-116.1 35.5-154.2 80.2-30.5 34.6-58.1 87.1-58.1 141.6 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.4 1.3 13.6 1.3 47.8 0 107.8-32.1 142.4-75.3z" />
                  </svg>
                  <div>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '9px',
                        lineHeight: 1,
                        marginBottom: '2px',
                      }}
                    >
                      DOWNLOAD ON THE
                    </div>
                    <div
                      style={{
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      App Store
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: phone */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                animation: 'downloadFloat 4s ease-in-out infinite',
                zIndex: 1,
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '380px',
                  height: '380px',
                  background:
                    'radial-gradient(circle, rgba(204,21,30,0.22) 0%, transparent 68%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }}
              />
              <img
                src="/userApp.webp"
                alt="Tringbox App"
                style={{
                  width: '320px',
                  height: 'auto',
                  filter: 'drop-shadow(0 24px 60px rgba(0,0,0,0.6))',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
          </div>
        )}

        {/* ── MOBILE / TABLET: stacked layout ── */}
        {!isDesktop && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: isMobile ? '16px 20px 0' : '32px 40px 0',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* glow orbs */}
            <div
              style={{
                position: 'absolute',
                top: '-5%',
                right: '-10%',
                width: '300px',
                height: '300px',
                background:
                  'radial-gradient(circle, rgba(204,21,30,0.14) 0%, transparent 65%)',
                pointerEvents: 'none',
              }}
            />

            {/* Logo */}
            <div
              style={{
                width: logoSize,
                height: logoSize,
                background: 'white',
                WebkitMask: 'url(/images/TringboxWhite.svg) no-repeat center',
                mask: 'url(/images/TringboxWhite.svg) no-repeat center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                flexShrink: 0,
                animation: 'downloadFadeUp 0.5s ease-out both',
                marginBottom: isMobile ? '12px' : '16px',
              }}
            />

            {/* Headline */}
            <h1
              style={{
                color: '#fff',
                fontSize: h1Size,
                fontWeight: 800,
                margin: isMobile ? '0 0 10px 0' : '0 0 14px 0',
                lineHeight: 1.15,
                letterSpacing: '-0.5px',
                textAlign: 'center',
                animation: 'downloadFadeUp 0.6s ease-out both',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
              }}
            >
              Your Music, Your Mood
            </h1>

            {/* Body text */}
            <p
              style={{
                color: '#e0e0e0',
                fontSize: bodySize,
                margin: isMobile ? '0 0 20px 0' : '0 0 28px 0',
                lineHeight: 1.75,
                textAlign: 'center',
                maxWidth: '480px',
                animation: 'downloadFadeUp 0.7s ease-out both',
              }}
            >
              Request your favourite songs, send shoutouts to friends, and set
              the vibe at live venues all from the{' '}
              <span style={{ color: '#fff', fontWeight: 700 }}>Tringbox</span>{' '}
              app.
            </p>

            {/* Download button */}
            <button
              className="dl-btn"
              onClick={handleDownload}
              style={{
                background: 'linear-gradient(90deg, #CC151E 0%, #8A325D 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '50px',
                padding: '15px 40px',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'Quicksand', sans-serif",
                animation: 'downloadPulse 3s ease-in-out infinite',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                position: 'relative',
                overflow: 'hidden',
                marginBottom: isMobile ? '14px' : '20px',
                letterSpacing: '0.3px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '60%',
                  height: '100%',
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                  animation: 'downloadShimmer 2.5s infinite',
                }}
              />
              <svg
                style={{ position: 'relative', zIndex: 1 }}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span style={{ position: 'relative', zIndex: 1 }}>
                Download the App
              </span>
            </button>

            {/* Store badges */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginBottom: isMobile ? '24px' : '36px',
                animation: 'downloadFadeUp 0.9s ease-out both',
              }}
            >
              <a
                className="dl-badge"
                href={playStoreURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...storeBadgeBase }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 20.5v-17c0-.83 1-.83 1.5-.5l15 8.5-15 8.5c-.5.33-1.5.33-1.5-.5z"
                    fill="#34A853"
                  />
                  <path d="M3 3.5L13.5 12 3 20.5V3.5z" fill="#FBBC04" />
                  <path d="M3 3.5L13.5 12 8 17.5 3 20.5V3.5z" fill="#EA4335" />
                  <path d="M3 20.5L8 15.5l5.5 6.5-10.5-1.5z" fill="#4285F4" />
                </svg>
                <div>
                  <div
                    style={{
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '8px',
                      lineHeight: 1,
                      marginBottom: '2px',
                    }}
                  >
                    GET IT ON
                  </div>
                  <div
                    style={{
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    Google Play
                  </div>
                </div>
              </a>
              <a
                className="dl-badge"
                href={appStoreURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...storeBadgeBase }}
              >
                <svg width="18" height="20" viewBox="0 0 814 1000" fill="white">
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 71 0 130.5 46.4 174.9 46.4 42.7 0 109.2-49.8 190.5-49.8zM642 110.3c32.5-38.2 56.3-91.4 56.3-144.6 0-7.7-.6-15.4-1.9-22.4-53.4 2-116.1 35.5-154.2 80.2-30.5 34.6-58.1 87.1-58.1 141.6 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.4 1.3 13.6 1.3 47.8 0 107.8-32.1 142.4-75.3z" />
                </svg>
                <div>
                  <div
                    style={{
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '8px',
                      lineHeight: 1,
                      marginBottom: '2px',
                    }}
                  >
                    DOWNLOAD ON THE
                  </div>
                  <div
                    style={{
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    App Store
                  </div>
                </div>
              </a>
            </div>

            {/* Features section (mobile/tablet: inline here) */}
            <div
              style={{ width: '100%', maxWidth: '560px', marginBottom: '36px' }}
            >
              <h2
                style={{
                  color: '#ffffff',
                  textAlign: 'center',
                  fontSize: isMobile ? '20px' : '26px',
                  fontWeight: 800,
                  marginBottom: '20px',
                  lineHeight: 1.25,
                  letterSpacing: '-0.3px',
                }}
              >
                Everything you need for the{' '}
                <span
                  style={{
                    background:
                      'linear-gradient(90deg, #CC151E 0%, #8A325D 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block',
                  }}
                >
                  perfect night out
                </span>
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '12px',
                }}
              >
                {features.map((item, idx) => (
                  <div
                    className="dl-card"
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                      background: 'rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      borderRadius: '16px',
                      padding: '16px',
                      border: '1px solid rgba(255,255,255,0.08)',
                      transition: 'all 0.3s ease',
                      animation: `downloadFadeUp ${0.5 + idx * 0.12}s ease-out both`,
                    }}
                  >
                    <div
                      style={{
                        width: '44px',
                        minWidth: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background:
                          'linear-gradient(135deg, rgba(204,21,30,0.25) 0%, rgba(138,50,93,0.2) 100%)',
                        border: '1px solid rgba(204,21,30,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        flexShrink: 0,
                      }}
                    >
                      {item.emoji}
                    </div>
                    <div>
                      <div
                        style={{
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '14px',
                          marginBottom: '4px',
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        style={{
                          color: '#c8c8c8',
                          fontSize: '12px',
                          lineHeight: 1.6,
                        }}
                      >
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone image at the bottom */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                animation: 'downloadFloat 4s ease-in-out infinite',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '30%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '260px',
                  height: '260px',
                  background:
                    'radial-gradient(circle, rgba(204,21,30,0.2) 0%, transparent 68%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }}
              />
              <img
                src="/userApp.webp"
                alt="Tringbox App"
                style={{
                  width: isMobile ? '220px' : '280px',
                  height: 'auto',
                  filter: 'drop-shadow(0 20px 50px rgba(0,0,0,0.5))',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
          </div>
        )}

        {/* ── FEATURES SECTION (desktop only) ── */}
        {isDesktop && (
          <div
            style={{
              background: 'transparent',
              position: 'relative',
              overflow: 'hidden',
              padding: '88px 100px 96px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background:
                  'linear-gradient(90deg, transparent, rgba(204,21,30,0.5), transparent)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '700px',
                height: '400px',
                background:
                  'radial-gradient(ellipse, rgba(204,21,30,0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />

            <h2
              style={{
                color: '#ffffff',
                textAlign: 'center',
                fontSize: '34px',
                fontWeight: 800,
                marginBottom: '48px',
                lineHeight: 1.25,
                position: 'relative',
                zIndex: 1,
                letterSpacing: '-0.3px',
              }}
            >
              Everything you need for the{' '}
              <span
                style={{
                  background:
                    'linear-gradient(90deg, #CC151E 0%, #8A325D 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                }}
              >
                perfect night out
              </span>
            </h2>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: featureCols,
                gap: '20px',
                maxWidth: '860px',
                margin: '0 auto',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {features.map((item, idx) => (
                <div
                  className="dl-card"
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '18px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    transition: 'all 0.3s ease',
                    animation: `downloadFadeUp ${0.5 + idx * 0.12}s ease-out both`,
                    cursor: 'default',
                  }}
                >
                  <div
                    style={{
                      width: '54px',
                      minWidth: '54px',
                      height: '54px',
                      borderRadius: '14px',
                      background:
                        'linear-gradient(135deg, rgba(204,21,30,0.25) 0%, rgba(138,50,93,0.2) 100%)',
                      border: '1px solid rgba(204,21,30,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '26px',
                      flexShrink: 0,
                    }}
                  >
                    {item.emoji}
                  </div>
                  <div>
                    <div
                      style={{
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '17px',
                        marginBottom: '5px',
                        letterSpacing: '0.1px',
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        color: '#c8c8c8',
                        fontSize: '14px',
                        lineHeight: 1.6,
                      }}
                    >
                      {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            padding: isMobile ? '20px 20px' : '28px 80px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            background: 'transparent',
          }}
        >
          <p
            style={{
              color: '#666',
              fontSize: '12px',
              margin: 0,
              textAlign: 'center',
            }}
          >
            Tringbox is available on iOS and Android
          </p>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <a
              href={playStoreURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#888',
                fontSize: '12px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 20.5v-17c0-.83 1-.83 1.5-.5l15 8.5-15 8.5c-.5.33-1.5.33-1.5-.5z"
                  fill="#34A853"
                />
                <path d="M3 3.5L13.5 12 3 20.5V3.5z" fill="#FBBC04" />
                <path d="M3 3.5L13.5 12 8 17.5 3 20.5V3.5z" fill="#EA4335" />
                <path d="M3 20.5L8 15.5l5.5 6.5-10.5-1.5z" fill="#4285F4" />
              </svg>
              Google Play
            </a>
            <span style={{ color: '#333' }}>|</span>
            <a
              href={appStoreURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#888',
                fontSize: '12px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              <svg width="12" height="14" viewBox="0 0 814 1000" fill="#aaa">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 71 0 130.5 46.4 174.9 46.4 42.7 0 109.2-49.8 190.5-49.8zM642 110.3c32.5-38.2 56.3-91.4 56.3-144.6 0-7.7-.6-15.4-1.9-22.4-53.4 2-116.1 35.5-154.2 80.2-30.5 34.6-58.1 87.1-58.1 141.6 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.4 1.3 13.6 1.3 47.8 0 107.8-32.1 142.4-75.3z" />
              </svg>
              App Store
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadApp;
